import { cn } from "@/lib/utils"

/**
 * `pill` is the default inline row. `tile` lays the options out as a grid of
 * bordered cards with a radio dot, for the handful of places where the choice
 * is the main thing on screen rather than one field among many.
 */
export type ChipGroupVariant = "pill" | "tile"

export type ChipOption<T> = {
  value: T
  label: string
}

type ChipGroupProps<T> = {
  options: ChipOption<T>[]
  value: T
  onChange: (value: T) => void
  name?: string
  disabled?: boolean
  variant?: ChipGroupVariant
}

const VARIANTS = {
  pill: {
    group: "flex flex-wrap gap-2",
    option: "rounded-full px-3 py-1.5",
    checked: "border-primary bg-primary text-primary-foreground",
    unchecked: "border-input bg-background hover:bg-accent",
  },
  tile: {
    group: "grid grid-cols-2 gap-2.5",
    option: "flex items-center gap-2.5 rounded-xl px-4 py-3.5 text-left",
    checked: "border-primary bg-primary/10 ring-primary/30 ring-1",
    // `bg-card` rather than the pill's `bg-background`, a grey in this theme
    // that made the tiles look disabled.
    unchecked: "hover:border-primary border-border bg-card hover:bg-accent",
  },
} as const

export function ChipGroup<T extends string | number | boolean>({
  options,
  value,
  onChange,
  name,
  disabled,
  variant = "pill",
}: ChipGroupProps<T>) {
  const styles = VARIANTS[variant]

  return (
    <div role="radiogroup" aria-label={name} className={styles.group}>
      {options.map((option) => {
        const checked = option.value === value
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "cursor-pointer border text-sm font-medium transition-colors",
              "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
              styles.option,
              checked ? styles.checked : styles.unchecked,
            )}
          >
            {variant === "tile" && (
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border",
                  checked
                    ? "border-primary bg-primary"
                    : "border-input bg-card",
                )}
              >
                {checked && (
                  <span className="bg-primary-foreground size-1.5 rounded-full" />
                )}
              </span>
            )}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
